import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { CadastroService } from 'src/app/cadastro/cadastro.service';
import { AutenticacaoService } from 'src/app/seguranca/autenticacao.service';
import { Bandeira } from 'src/app/shared/models/bandeira.enum';
import { LoginAutomatico } from 'src/app/shared/models/login-automatico.model';
import { Usuario } from 'src/app/shared/models/usuario.model';
import { BannerPortalService } from 'src/app/shared/services/banner-portal.service';
import { GoogleAnalyticsService } from 'src/app/shared/services/google-analytics.service';
import { PasswordRecoveryService } from 'src/app/shared/services/password-recovery.service';
import { ToastService } from 'src/app/shared/toast';

const assets = {
  cb: {
    secondCopy: 'assets/images/icons/ic-cb-segunda-via.svg',
    renegotiation: 'assets/images/icons/ic-cb-renegociacao-home.svg',
    specialDiscounts: 'assets/images/icons/ic-cb-desconto-especiais.svg',
    brandMascot: 'assets/images/login/cb-login-section.png',
    installmentIcon: 'assets/images/icons/ic-cb-parcelamento.svg'
  },
  pf: {
    secondCopy: 'assets/images/icons/ic-pf-segunda-via.svg',
    renegotiation: 'assets/images/icons/ic-pf-renegociacao-home.svg',
    specialDiscounts: 'assets/images/icons/ic-pf-desconto-especiais.svg',
    brandMascot: 'assets/images/login/pf-login-section.png',
    installmentIcon: 'assets/images/icons/ic-pf-parcelamento.svg'
  }
}

@Component({
  selector: 'app-login-page',
  templateUrl: './login-page.component.html',
  styleUrls: ['./login-page.component.scss']
})
export class LoginPageComponent implements OnInit {
  isCasasBahia = location.href.indexOf('casasbahia') > 0 || location.href.indexOf('-cb') > 0;
  imageBannerPortal: SafeResourceUrl;
  subscriptions = new Subscription();

  login = true;
  isLoginAutomatico = false;
  tokenParceiro: string;
  loading = false;
  networkError = null;

  private readonly BUSINESS_HTTP_ERROR = 422;

  constructor(
    private bannerPortalService: BannerPortalService,
    private passwordRecoveryService: PasswordRecoveryService,
    private cadastroService: CadastroService,
    private googleAnalyticsService: GoogleAnalyticsService,
    private router: Router,
    private route: ActivatedRoute,
    private autenticacaoService: AutenticacaoService,
    private toastService: ToastService,
  ) { }

  ngOnInit(): void {
    this.initialize();
  }

  initialize() {
    this.getImageBannerPortalUrl();
    this.passwordRecoveryService.clearStorageData();
    this.cadastroService.clearStorageData();
    this.googleAnalyticsService.pushNavigateEvent('/renegociacao/acesso/login');
    this.handleAutoLogin();
  }

  handleAutoLogin() {
    this.subscriptions.add(this.route.queryParams.subscribe(({ parceiro, login, cp, redirectToken, auth }) => {
      this.tokenParceiro = parceiro;
      this.isLoginAutomatico = !!parceiro || !!cp || !!login || !!redirectToken;

      if (login) {
        this.autoLogin(login, auth);
      }

      if (cp) {
        this.autoLoginCampanha(cp);
      }

      if (redirectToken) {
        this.autoLoginWithRedirectToken(redirectToken);
      }
    }));
  }

  async autoLoginWithRedirectToken(redirectToken: string): Promise<void> {
    try {
      this.loading = true;
      const rota = await this.autenticacaoService.loginAutomaticoWithToken(redirectToken);
      this.router.navigate([rota]);
    } catch (erro) {
      this.autenticacaoService.logout();
      this.toastService.show({ title: 'Login automático inválido', text: 'Por favor tente se autenticar manualmente.', type: 'error' });
    } finally {
      this.loading = false;
    }
  }

  async autoLogin(base64LoginAutomatico: string, authTicket?: string): Promise<void> {
    try {
      this.loading = true;
      const rota = await this.autenticacaoService.loginAutomatico(
        LoginAutomatico.parseBase64(base64LoginAutomatico),
        authTicket,
        this.tokenParceiro
      );

      this.router.navigate([rota]);
    } catch (error) {
      this.autenticacaoService.logout();
      if (error instanceof HttpErrorResponse) {
        if (error.status === this.BUSINESS_HTTP_ERROR) {
          this.toastService.show({ title: 'Nenhum carnê encontrado', text: 'Você não tem compra ativa com carnê', type: 'error' });
          return;
        }
        if (error?.error?.message) {
          this.toastService.show({ title: error.error.message, type: 'error' });
          return;
        }
      }
      this.toastService.show({ title: 'Erro na autenticação automática', text: 'Por favor tente se autenticar manualmente.', type: 'error' });
    } finally {
      this.loading = false;
    }
  }

  async autoLoginCampanha(idCampanha: string): Promise<void> {
    try {
      this.loading = true;
      const rota = await this.autenticacaoService.loginCampanha(idCampanha, this.tokenParceiro);
      this.router.navigate([rota]);
    } catch (erro) {
      this.autenticacaoService.logout();
      this.toastService.show({ title: 'Campanha inválida', text: 'Por favor tente se autenticar manualmente.', type: 'error' });
    } finally {
      this.loading = false;
    }
  }

  async handleLogin(user: Usuario): Promise<void> {
    try {
      this.loading = true;
      this.networkError = null;
      await this.autenticacaoService.login(user, this.tokenParceiro);
      this.router.navigate(['/portal']);
    } catch (response) {
      this.networkError = 'A senha não está correta';
    } finally {
      this.loading = false;
    }
  }

  get assets() {
    if (this.isCasasBahia) {
      return assets.cb;
    }

    return assets.pf;
  }

  async getImageBannerPortalUrl() {
    try {
      const bandeira = this.isCasasBahia ? Bandeira.CASAS_BAHIA : Bandeira.PONTOFRIO;
      this.imageBannerPortal = await this.bannerPortalService.getImageBannerPortalUrl(bandeira);
    } catch (error) { }
  }

  handleScrollToLoginForm() {
    document
      .getElementById("form-section")
      .scrollIntoView({ behavior: "smooth" });
  }
}
